import { useEffect, useState } from 'react';
import { useStore } from '../store';
import { Button } from '../components/ui';
import { getStripePrices, initiateCheckoutWithPriceId, StripePrice } from '../services/api';
import frontGuide from '../assets/onboarding/front.png';
import { trackEvent } from '../analytics';

const BLURRED_CATEGORIES_PSL = [
  { name: 'PSL Tier', blur: '6.7' },
  { name: 'Genetic Ceiling', blur: '7.2' },
  { name: 'Primary Strength', blur: '8.0' },
  { name: 'Looksmax Priority', blur: '7.6' },
];

const BLURRED_CATEGORIES_NORMAL = [
  { name: 'Jawline', blur: '8.2' },
  { name: 'Masculinity', blur: '7.5' },
  { name: 'Skin Quality', blur: '6.9' },
  { name: 'Cheekbones', blur: '7.8' },
];

const ONE_TIME_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ONE_TIME as string;

export function PaywallScreen() {
  const { user, rating, onboarding } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [price, setPrice] = useState<StripePrice | null>(null);
  const [isPriceLoading, setIsPriceLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    getStripePrices()
      .then((prices) => {
        if (isMounted) {
          const oneTimePrice = prices.find((p) => p.id === ONE_TIME_PRICE_ID);
          setPrice(oneTimePrice || null);
          setIsPriceLoading(false);
        }
      })
      .catch((err) => {
        console.error('Failed to load price', err);
        if (isMounted) {
          setError('Unable to load pricing.');
          setIsPriceLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const formatPrice = (p: StripePrice) => {
    if (p.unitAmount == null) return '';
    return `$${(p.unitAmount / 100).toFixed(2)}`;
  };

  const handleContinue = async () => {
    if (!price) return;
    setIsLoading(true);
    setError(null);

    try {
      trackEvent('paywall_continue_click', {
        priceId: price.id,
        amount: price.unitAmount ?? null,
        currency: price.currency,
      });
      const checkoutUrl = await initiateCheckoutWithPriceId(user.userId, price.id);
      window.location.href = checkoutUrl;
    } catch (err) {
      setError('Unable to start checkout. Please try again.');
      console.error('Checkout error:', err);
      trackEvent('paywall_checkout_error');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-black">
      {/* NO CLOSE BUTTON - This is a hard paywall */}

      <div className="flex-1 flex flex-col px-6 py-8 max-w-md mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/20 bg-surface">
              <img
                src={rating.frontPhotoPreview || frontGuide}
                alt="User"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2">Unlock Your Facial Potential</h1>
          <p className="text-secondary text-lg">See your ratings</p>
        </div>

        {/* Blurred rating cards */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {(onboarding.ratingType === 'psl' ? BLURRED_CATEGORIES_PSL : BLURRED_CATEGORIES_NORMAL).map((category) => (
            <div
              key={category.name}
              className="p-4 rounded-xl bg-surface border border-default relative overflow-hidden"
            >
              <p className="text-sm text-secondary mb-1">{category.name}</p>
              <div className="flex items-baseline gap-1">
                <span
                  className="text-2xl font-bold text-white"
                  style={{ filter: 'blur(8px)', userSelect: 'none' }}
                >
                  {category.blur}
                </span>
                <span className="text-sm text-muted">/10</span>
              </div>
              {/* Lock overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <svg className="w-5 h-5 text-white/50" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1a5 5 0 00-5 5v4H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V12a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 9H9V6a3 3 0 116 0v4z" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* One-time price display */}
        <div className="space-y-3 mb-8">
          {isPriceLoading ? (
            <div className="w-full p-5 rounded-xl border-2 border-default bg-surface">
              <div className="h-6 w-28 rounded-full shimmer mb-3" />
              <div className="h-4 w-40 rounded-full shimmer" />
            </div>
          ) : price ? (
            <div className="w-full p-5 rounded-xl border-2 border-accent bg-accent/10 ring-2 ring-accent/30 text-left relative">
              <span className="absolute -top-3 left-4 px-3 py-1 bg-accent text-black text-xs font-bold rounded-full">
                ONE-TIME
              </span>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold">{formatPrice(price)}</span>
                    <span className="text-secondary">{price.currency.toUpperCase()}</span>
                  </div>
                  <p className="text-sm text-secondary mt-1">One-time unlock. Save your results.</p>
                </div>
                <div className="w-6 h-6 rounded-full border-2 border-accent flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-accent" />
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {error && (
          <p className="text-red-500 text-sm text-center mb-4">{error}</p>
        )}

        <Button onClick={handleContinue} isLoading={isLoading} disabled={isPriceLoading || !price}>
          Continue
        </Button>

        {/* Fine print */}
        <p className="text-xs text-muted text-center mt-4 px-4">
          Subscription automatically renews unless canceled at least 24 hours before the end of the current period.
        </p>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-default">
        <div className="flex justify-center gap-6 text-sm">
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted hover:text-secondary"
          >
            Terms of Service
          </a>
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted hover:text-secondary"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
}
